import { useLocation } from 'react-router';

import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import { useProfessionalInitialRouteData } from '@modules/professional/hook/use-professional-initial-route-data';
import { useProfileFamily } from '@modules/professional/hook/use-profile-family';
import { professionalTabBase } from '@modules/professional/util/professional-tab-base';
import { ProfileFamilyMarks } from './ProfileFamilyMarks';

/** Naming more than this pushes the line past its two-line budget. */
const MAX_NAMED = 3;

/**
 * The one-line family signal under the logo cluster (Figma 5543:100730) —
 * "Surestyle is part of a Brand Group with: Lundia, Moduspace". The profile's
 * own name is inked and the sentence grey, so the line reads as a caption on the
 * marks above it rather than a heading.
 *
 * It links to the Network tab, where the whole family is tiled out — the line
 * can only name the first few brands, so it hands off rather than growing. It
 * shares the sub-nav row with the tab pills and is the element that flexes
 * there: it wraps to at most two lines so the pills never wrap. Renders nothing
 * without a family.
 */
export const ProfileFamilyLine = ({
    className,
    theme = 'light',
    withMarks = false,
}: {
    className?: string;
    /** `dark` for the inverted sub-nav on the dark tabs. */
    theme?: 'light' | 'dark';
    /**
     * Lead with a stack of the sibling brand logos — the mobile "social backing"
     * form, which sits under the trust badges and carries the marks itself since
     * the mobile banner has none.
     */
    withMarks?: boolean;
}) => {
    const family = useProfileFamily();
    const { Title, URLSegment } = useProfessionalInitialRouteData();
    const { pathname } = useLocation();
    if (!family) return null;
    const isDark = theme === 'dark';

    const named = family.others
        .slice(0, MAX_NAMED)
        .map((brand) => brand.title)
        .join(', ');
    const extra = family.others.length - MAX_NAMED;

    return (
        <RoccoLink
            // Preview-aware: hardcoding the public path sent the Business
            // Manager preview iframe out onto the live site, ending the
            // author's preview session.
            to={`${professionalTabBase(URLSegment, pathname)}/partners`}
            prefetch="intent"
            // Matches the tab pills, which now set this too: `useTabScrollAnchor`
            // aligns the nav to the viewport top on any same-profile tab change,
            // however it was triggered, so the router's own top reset would only
            // add a jump before the anchor lands.
            preventScrollReset
            className={cn(
                'm-0 font-sans text-[0.9375rem] leading-[1.22] tracking-[-0.01em]',
                // The clamp lives on the text when the marks lead, so the row
                // stays a flex line and the stack isn't clipped with the copy.
                //
                // Weight differs by form: the Figma sub-nav line is Medium
                // throughout (2354:55431 sibling, 65 Medium), but the mobile
                // backing line sits among the trust badges, which set their copy
                // at regular and reserve Medium for the salient value — so it
                // follows them and only the profile's own name goes Medium.
                withMarks
                    ? 'flex items-center gap-2 font-normal'
                    : 'line-clamp-2 font-medium',
                className,
            )}
        >
            {withMarks ? (
                <ProfileFamilyMarks
                    brands={family.others}
                    interactive={false}
                    className="-space-x-2"
                    markClassName="size-7 rounded-full ring-2 ring-white"
                />
            ) : null}
            <span className={cn('min-w-0', withMarks && 'line-clamp-2')}>
                <span
                    className={cn(
                        'font-medium',
                        isDark ? 'text-white' : 'text-[#181818]',
                    )}
                >
                    {Title}
                </span>
                <span className={isDark ? 'text-white/60' : 'text-[#9a9a9a]'}>
                    {' is part of a Brand Group with: '}
                    {named}
                    {extra > 0 ? ` +${extra} more` : ''}
                </span>
            </span>
        </RoccoLink>
    );
};
